import { createDebugger } from '~/scripts/utils/debug.js';
import { populateTemplate } from '~/scripts/utils/populate-template.js';

const debug = createDebugger('menu-info', false);

/**
 * Prefixes a root-relative URL with the active locale root (/en/, /fi/).
 * Shortcut URLs come from metafields as plain text, so Shopify never
 * localizes them. Mirrors snippets/bf2-lokal-url.liquid.
 */
function localizeUrl(url) {
    const root = window.Shopify?.routes?.root || '/';
    if (!url || root === '/' || !url.startsWith('/') || url.startsWith('//')) return url;
    if (url === root.slice(0, -1) || url.startsWith(root)) return url;
    return root + url.slice(1);
}

/**
 * Parses the raw menu_info JSON (from api-metaobject section) into a usable
 * fields object shared by both the desktop mega menu and mobile nav drawer.
 */
export function parseMenuInfoFields(data) {
    return {
        description: data.description || '',
        subtext:     data.subtext || '',
        image:       data.image_src ? { src: data.image_src, alt: data.image_alt || '' } : null,
        shortcuts:   Array.isArray(data.shortcuts)
            ? data.shortcuts.map(s => ({ url: localizeUrl(s.url) || '#', label: s.label || s.text || '' }))
            : [],
    };
}

/**
 * Fetches menu_info for a collection/page via the Section Rendering API.
 * Returns { data, fields } or null if no metafield is present.
 *
 * @param {string} url - The collection/page URL (e.g. /collections/inredning)
 */
export async function fetchMenuInfo(url) {
    const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}sections=api-metaobject`;
    debug.log('fetch', { url: fetchUrl });

    const res = await fetch(fetchUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = await res.json();
    const sectionHtml = json['api-metaobject'] || '';

    const tmp = document.createElement('div');
    tmp.innerHTML = sectionHtml;

    const debugTag = tmp.querySelector('script[data-debug]');
    if (debugTag) debug.log('section debug', JSON.parse(debugTag.textContent));

    const scriptTag = tmp.querySelector('script[type="application/json"]:not([data-debug])');
    if (!scriptTag) return null; // metafield is blank

    const data = JSON.parse(scriptTag.textContent);
    debug.log('parsed data', data);
    return { data, fields: parseMenuInfoFields(data) };
}

/**
 * Populates a <template> with menu_info data and appends the result to
 * `container`. Reveals [data-field] elements that have content.
 *
 * @param {HTMLElement}         container        - Element to append the rendered content to
 * @param {HTMLTemplateElement} infoTemplate     - Template with {# var #} placeholders
 * @param {HTMLTemplateElement} shortcutTemplate - Template for a single shortcut link
 * @param {{ data, fields }}    result           - Return value of fetchMenuInfo()
 */
export function renderMenuInfo(container, infoTemplate, shortcutTemplate, { data, fields }) {
    const fragment = populateTemplate(infoTemplate, {
        description: fields.description  || '',
        subtext:     fields.subtext      || '',
        image_src:   fields.image?.src   || '',
        image_alt:   fields.image?.alt   || '',
        title:       data.title          || '',
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'h-full';
    wrapper.appendChild(fragment);

    // Show elements whose data is present; leave others hidden (js-hidden)
    wrapper.querySelectorAll('[data-field]').forEach((el) => {
        const key = el.dataset.field;
        const hasContent =
            key === 'shortcuts' ? fields.shortcuts.length > 0 :
            key === 'image'     ? !!fields.image?.src            :
            key === 'title'     ? !!data.title                   :
            key === 'subtext'   ? !!fields.subtext               :
            !!fields[key];
        if (hasContent) el.classList.remove('js-hidden');
    });

    if (fields.shortcuts.length > 0 && shortcutTemplate) {
        const list = wrapper.querySelector('[data-shortcuts-list]');
        if (list) {
            fields.shortcuts.forEach((s) => {
                list.appendChild(populateTemplate(shortcutTemplate, {
                    url:   s.url   || '#',
                    label: s.label || '',
                }));
            });
        }
        // Show the outer shortcuts column (shared with vendors)
        wrapper.querySelector('[data-shortcuts-col]')?.classList.remove('js-hidden');
    }

    container.appendChild(wrapper);
}
